import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of, tap } from 'rxjs';
import { TrainingLevelLimitsApi } from './training-level-limits.api';
import {
  EMPTY_TRAINING_LEVEL_LIMITS,
  ITrainingLevelLimit,
  ITrainingLevelLimitPatchItem,
} from './training-level-limits.model';

/**
 * Page-scoped store for the "manage limits" sub-view of a training level. Provided in the
 * limits component's `providers` (never `providedIn: 'root'`) so it is created and destroyed
 * with that view. Driven by `levelId` (set once, from the category being managed) and
 * `sportId` (set from the sport picker) — limits are only fetched once both are set.
 */
@Injectable()
export class TrainingLevelLimitsStore {
  private readonly api = inject(TrainingLevelLimitsApi);

  private readonly _levelId = signal<number | null>(null);
  private readonly _sportId = signal<number | null>(null);

  readonly sportId = this._sportId.asReadonly();

  private readonly _limits = rxResource({
    params: () => ({ levelId: this._levelId(), sportId: this._sportId() }),
    stream: ({ params }) =>
      params.levelId === null || params.sportId === null
        ? of(EMPTY_TRAINING_LEVEL_LIMITS)
        : this.api.getLimits(params.levelId, params.sportId),
  });

  readonly limits = computed<ITrainingLevelLimit[]>(
    () => this._limits.value() ?? EMPTY_TRAINING_LEVEL_LIMITS,
  );
  readonly isLoading = computed(() => this._limits.isLoading());
  readonly error = computed(() => this._limits.error());

  setLevelId(levelId: number): void {
    this._levelId.set(levelId);
  }

  setSportId(sportId: number | null): void {
    this._sportId.set(sportId);
  }

  save(items: readonly ITrainingLevelLimitPatchItem[]): Observable<ITrainingLevelLimit[]> {
    const levelId = this._levelId();
    if (levelId === null) {
      throw new Error('TrainingLevelLimitsStore.save called before a level was set.');
    }

    return this.api.patchLimits(levelId, items);
  }
}
